<script lang="ts" setup>

import { computed, storeToRefs, useNuxtApp } from '#imports';
import { useUserStore } from '~/stores/user/useUserStore';

const userStore = useUserStore();

const { $wagmi } = useNuxtApp();

const {
    network,
    user,
    chainId
} = storeToRefs(userStore);

const isOpen = computed(() => !!$wagmi?.networksList.isOpenNetworkList.value);

</script>

<template>
  <Dropdown :is-open="isOpen"
            dropdown-class="button"
            @update:is-open="$wagmi?.networksList.setVisibleNetworkList"
  >
    <template #toggle>
      <Transition mode="out-in"
                  name="fade"
      >
        <div v-if="chainId && network"
             :aria-label="$wagmi?.networksList.isOpenNetworkList.value ? 'Hide network list' : 'Show network list'"
             :title="$wagmi?.networksList.isOpenNetworkList.value ? 'Hide network list' : 'Show network list'"
             :class="{ 'button-icon--active': $wagmi?.networksList.isOpenNetworkList.value }"
             class="button-icon display-flex align-items-center justify-center cursor-pointer display-none-bp-576"
             role="button"
             type="button"
             @click="$wagmi?.networksList.toggleOpenNetworkList"
        >
          <IconMain :icon="network.toUpperCase()"
                    :size="24"
          />
        </div>
        <button v-else-if="user && !network"
                aria-label="switch network"
                class="display-flex flex-col button-switch cursor-pointer "
                type="button"
                @click="$wagmi?.networksList.toggleOpenNetworkList"
        >
          <IconMain :size="16"
                    class="button-switch__icon"
                    icon="switch"
          />
          <span class="compact-500-accent">Switch</span>
        </button>
      </Transition>
    </template>
    <template #content>
      <Suspense>
        <template #fallback>
          <div class="suspense-helper">
            <LoaderDots />
          </div>
        </template>
        <LazyWeb3ChainList />
      </Suspense>
    </template>
  </Dropdown>
  <ClientOnly>
    <template #fallback></template>
    <Teleport to="body">
      <LazyModalSwitchNetwork />
    </Teleport>
  </ClientOnly>
</template>

<style lang="scss" scoped>

.suspense-helper {
  width: 348px;
  padding-top: 24px;
  padding-bottom: 24px;
  border-radius: var(--background-500-radius, 4px);
  background: var(--layer-floor-2);
}

.button {
  align-self: stretch;

  &-icon {
    width: 52px;
    height: 52px;
    transition: all 0.3s ease-in-out;
    border: 1px solid transparent;
    border-radius: 4px;
    background-color: transparent;

    &--active {
      background-color: var(--bg-secondary);
    }
  }

  &-switch {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 125px;
    height: 100%;
    border-radius: var(--control-300-radius, 2px);
    background-color: var(--bg-brand-mojito-primary);

    @include mobile-576 {
      display: none;
    }

    &__icon {
      margin-right: 12px;
    }
  }
}

.fade-enter-active {
  transition: all 0.2s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
